import React from "react";
import { Modal, Button, Badge } from "react-bootstrap";
import {
  FaCheckCircle,
  FaUser,
  FaTimes,
  FaExclamationTriangle
} from "react-icons/fa";

const formatCustomDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;

  const getOrdinal = (n) => {
    if (n > 3 && n < 21) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  };

  return `${getOrdinal(day)} ${month} ${year}, ${hours}:${minutes}${ampm}`;
};

const ApprovedLeavesInfoModal = ({
  isClose,
  isOpen,
  rejectModalOpen,
  selectedCard,
  handleCorrection,
}) => {
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};

  const parseLeaveDetails = (request) => {
    if (!request) {
      return {
        dates: [], days: [], types: [], totalDays: 0,
        wfhdates: [], wfhdays: [], totalWFHDays: 0,
        compWorkingDates: [], compOffDates: [], compDurations: [],
        compRequestTypes: [], compAmounts: [], totalCompDays: 0
      };
    }

    const dates = request.leave_dates
      ? request.leave_dates.split(',').map(d => d.trim()) : [];
    const wfhdates = request.wfh_dates
      ? request.wfh_dates.split(',').map(d => d.trim()) : [];
    const days = request.leave_days
      ? request.leave_days.split(',').map(d => d.trim()) : [];
    const wfhdays = request.wfh_days
      ? request.wfh_days.split(',').map(d => d.trim()) : [];
    const types = request.leave_day_types
      ? request.leave_day_types.split(',').map(t => t.trim()) : [];
    const compWorkingDates = request.comp_working_dates
      ? request.comp_working_dates.split(',').map(d => d.trim()) : [];
    const compOffDates = request.comp_off_dates
      ? request.comp_off_dates.split(',').map(d => d.trim()) : [];
    const compDurations = request.comp_durations
      ? request.comp_durations.split(',').map(d => d.trim()) : [];
    const compRequestTypes = request.comp_request_types
      ? request.comp_request_types.split(',').map(t => t.trim()) : [];
    const compAmounts = request.compensation_amounts
      ? request.compensation_amounts.split(',').map(a => a.trim()) : [];

    const totalDays = days.reduce((sum, day) => {
      return day.toLowerCase() === "half" ? sum + 0.5 : sum + 1;
    }, 0);

    const totalWFHDays = wfhdays.reduce((sum, day) => {
      return day.toLowerCase() === "half" ? sum + 0.5 : sum + 1;
    }, 0);

    const totalCompDays = compDurations.reduce((sum, duration) => {
      const value = duration?.toLowerCase() || '';
      if (value.includes('half') || value.includes('0.5') || value === 'half day') return sum + 0.5;
      if (value.includes('full') || value.includes('1') || value === 'full day') return sum + 1;
      const numericMatch = value.match(/(\d+(\.\d+)?)/);
      if (numericMatch) return sum + parseFloat(numericMatch[1]);
      return sum + 1;
    }, 0);

    return {
      dates, days, types, totalDays,
      wfhdates, wfhdays, totalWFHDays,
      compWorkingDates, compOffDates, compDurations,
      compRequestTypes, compAmounts, totalCompDays
    };
  };

  // ✅ Parse travel records from SQL GROUP_CONCAT columns
  const parseTravelDetails = (request) => {
    if (!request?.travel_locations) return [];

    const locations    = request.travel_locations.split(', ').map(d => d.trim());
    const fromDTs      = request.travel_from_datetimes?.split(', ').map(d => d.trim()) || [];
    const toDTs        = request.travel_to_datetimes?.split(', ').map(d => d.trim())   || [];
    const approvedList = request.travel_approved_statuses?.split(', ').map(s => s.trim() === '1') || [];

    return locations.map((loc, i) => ({
      location:     loc,
      fromDateTime: fromDTs[i]      || '',
      toDateTime:   toDTs[i]        || '',
      approved:     approvedList[i] || false
    }));
  };

  // ✅ Format "2026-02-17 09:00:00" or "2026-02-17T09:00" nicely
  const formatDateTime = (datetimeString) => {
    if (!datetimeString) return '—';
    try {
      const date = new Date(datetimeString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return datetimeString;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const calculateTotalDays = (data) => {
    if (Array.isArray(data)) {
      return data.reduce((total, rec) => {
        if (!rec?.leaveDay) return total + 1;
        return rec.leaveDay.toLowerCase() === "half" ? total + 0.5 : total + 1;
      }, 0);
    }
    if (typeof data === 'object' && data !== null && data.leave_type === "Work From Home Request") {
      return parseLeaveDetails(data).totalWFHDays;
    }
    if (typeof data === 'object' && data !== null) {
      return parseLeaveDetails(data).totalDays;
    }
    return 0;
  };

  const leaveDetails  = parseLeaveDetails(selectedCard);
  const travelRecords = parseTravelDetails(selectedCard);

  return (
    <Modal show={isOpen} onHide={isClose} centered size="lg">
      {/* Header */}
      <Modal.Header className="bg-success text-white border-0 py-2">
        <div className="d-flex align-items-center">
          <FaCheckCircle className="me-2 fs-5" />
          <div>
            <Modal.Title className="mb-0 fs-6">Approved Request</Modal.Title>
            <small className="opacity-75 small d-block">{formatCustomDate(selectedCard?.created_at)}</small>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-3">
        {/* Employee Info */}
        <div className="d-flex align-items-center mb-3 p-2 border rounded bg-light">
          <div
            className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ width: '40px', height: '40px' }}
          >
            <FaUser className="text-success" />
          </div>
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-semibold">{selectedCard?.from_name}</h6>
              <Badge bg="success" className="px-2 py-1">{selectedCard?.leave_type}</Badge>
            </div>
            <div className="d-flex flex-wrap gap-2 mt-1 small text-muted">
              <span className="d-flex align-items-center">
                <span className="text-truncate"><strong>To: </strong>{selectedCard?.to_email}</span>
              </span>
            </div>
            <div className="d-flex flex-wrap gap-2 mt-1 small text-muted">
              <span className="d-flex align-items-center">
                <span className="text-truncate"><strong>Subject: </strong>{selectedCard?.subject}</span>
              </span>
            </div>
          </div>
        </div>

        {/* WFH Details */}
        {selectedCard?.wfh_dates && selectedCard.wfh_dates.length > 0 && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="small mb-0">WFH Details</h6>
              <Badge bg="info" className="small px-2 py-1">
                {leaveDetails.totalWFHDays} day{leaveDetails.totalWFHDays !== 1 ? 's' : ''}
              </Badge>
            </div>
            <div className="border rounded-2 overflow-hidden" style={{ fontSize: '0.7rem' }}>
              <div className="row g-0 border-bottom bg-light">
                <div className="col-5 py-1 border-end px-2 fw-bold text-dark">Date</div>
                <div className="col-3 py-1 text-center fw-bold text-dark">Day</div>
              </div>
              {leaveDetails.wfhdates.map((date, index) => (
                <div key={index} className="row g-0 border-bottom">
                  <div className="col-5 py-1 border-end px-2 fw-medium">{formatDate(date)}</div>
                  <div className="col-3 py-1 text-center">{leaveDetails.wfhdays[index] || "Full"}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Leave Details */}
        {selectedCard?.leave_dates && selectedCard.leave_dates.length > 0 && !selectedCard?.leave_type?.includes('Compensatory') && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="small mb-0">Leave Details</h6>
              <Badge bg="info" className="small px-2 py-1">
                {leaveDetails.totalDays} day{leaveDetails.totalDays !== 1 ? 's' : ''}
              </Badge>
            </div>
            <div className="border rounded-2 overflow-hidden" style={{ fontSize: '0.7rem' }}>
              <div className="row g-0 border-bottom bg-light">
                <div className="col-5 py-1 border-end px-2 fw-bold text-dark">Date</div>
                <div className="col-3 py-1 border-end text-center fw-bold text-dark">Type</div>
                <div className="col-3 py-1 text-center fw-bold text-dark">Day</div>
              </div>
              {leaveDetails.dates.map((date, index) => (
                <div key={index} className="row g-0 border-bottom">
                  <div className="col-5 py-1 border-end px-2 fw-medium">{formatDate(date)}</div>
                  <div className="col-3 py-1 border-end text-center">{leaveDetails.types[index] || '—'}</div>
                  <div className="col-3 py-1 text-center">{leaveDetails.days[index] || 'Full'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compensatory Off Details */}
        {selectedCard?.comp_working_dates && selectedCard.comp_working_dates.length > 0 && selectedCard?.leave_type?.includes('Compensatory') && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="small mb-0">Compensatory Off Details</h6>
              <Badge bg="warning" className="small px-2 py-1">
                {leaveDetails.totalCompDays} day{leaveDetails.totalCompDays !== 1 ? 's' : ''}
              </Badge>
            </div>
            <div className="border rounded-2 overflow-hidden" style={{ fontSize: '0.7rem' }}>
              <div className="row g-0 border-bottom bg-light">
                <div className="col-4 py-1 border-end px-2 fw-bold text-dark">Working Date</div>
                <div className="col-4 py-1 border-end text-center fw-bold text-dark">Comp Off Date</div>
                <div className="col-4 py-1 border-end text-center fw-bold text-dark">Duration</div>
              </div>
              {leaveDetails.compWorkingDates.map((workingDate, index) => (
                <div key={index} className="row g-0 border-bottom">
                  <div className="col-4 py-1 border-end px-2 fw-medium">{formatDate(workingDate)}</div>
                  <div className="col-4 py-1 border-end text-center">
                    {leaveDetails.compOffDates[index] ? formatDate(leaveDetails.compOffDates[index]) : 'Not specified'}
                  </div>
                  <div className="col-4 py-1 border-end text-center">
                    {leaveDetails.compDurations[index] || 'Full Day'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ✅ Travel (Site Visit) Details */}
        {travelRecords.length > 0 && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="small mb-0">🚗 Travel (Site Visit) Details</h6>
              <Badge bg="warning" text="dark" className="small px-2 py-1">
                {travelRecords.length} record{travelRecords.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            <div className="border rounded-2 overflow-hidden" style={{ fontSize: '0.7rem' }}>
              <div className="row g-0 border-bottom bg-light">
                <div className="col-4 py-1 border-end px-2 fw-bold text-dark">Location</div>
                <div className="col-4 py-1 border-end text-center fw-bold text-dark">From</div>
                <div className="col-4 py-1 border-end text-center fw-bold text-dark">To</div>
              </div>
              {travelRecords.map((record, index) => (
                <div key={index} className="row g-0 border-bottom">
                  <div className="col-4 py-1 border-end px-2 fw-medium">{record.location}</div>
                  <div className="col-4 py-1 border-end text-center">{formatDateTime(record.fromDateTime)}</div>
                  <div className="col-4 py-1 border-end text-center">{formatDateTime(record.toDateTime)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Message */}
        {selectedCard?.body && selectedCard.body.trim() && (
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <span className="small fw-semibold">Message</span>
            </div>
            <div
              className="border rounded p-2 bg-light"
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: "200px",
                overflowY: "auto",
                lineHeight: "1.4",
                fontSize: "0.85rem"
              }}
            >
              {selectedCard.body.trim()}
            </div>
          </div>
        )}

        {/* Correction Notice */}
        {selectedCard?.is_corrected === "yes" && (
          <div className="alert alert-info small d-flex align-items-center border-0 p-2 mb-3">
            <FaExclamationTriangle className="me-2" size={14} />
            <div>
              <span className="fw-semibold">Correction Applied</span>
              <div className="small">This request has been corrected.</div>
            </div>
          </div>
        )}
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer className="border-top py-2">
        <div className="d-flex gap-2 w-100">
          <Button variant="outline-secondary" className="flex-grow-1 py-1" onClick={isClose}>
            Close
          </Button>
          {storedUser.role != 2 && selectedCard?.is_corrected === "no" && (
            <Button
              variant="primary"
              className="flex-grow-1 py-1"
              onClick={() => { handleCorrection(selectedCard.request_id); isClose(); }}
            >
              Mark Correction
            </Button>
          )}
          {storedUser.role != 2 && (
            <Button
              variant="outline-danger"
              className="flex-grow-1 py-1 d-flex align-items-center justify-content-center"
              onClick={rejectModalOpen}
            >
              <FaTimes className="me-1" size={12} />
              Reject
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ApprovedLeavesInfoModal;